import React from "react";
import { PhotoIcon } from "@heroicons/react/24/solid";
import PrimaryButton from "./PrimaryButton";
import SecondaryButton from "./SecondaryButton";
import useFileUpload from "../../hooks/useFileUpload";

const ImageUpload = React.forwardRef(
  (
    {
      onRemoveHandler,
      onRemoveConfirmHandler,
      filelist,
      defaulturl,
      label,
      description,
      uniqueId = "image-upload",
      optional = false,
      ...props
    },
    ref
  ) => {
    const { previewUrl, handleRemove, handleChange } = useFileUpload({
      defaultUrl: defaulturl,
      fileList: filelist,
      onRemoveHandler,
      onRemoveConfirmHandler,
      inputId: uniqueId,
    });

    return (
      <div>
        <label
          htmlFor={uniqueId}
          className="block text-sm font-medium text-ink-900 mb-2"
        >
          {label}
          {optional && (
            <span className="ml-1 font-normal text-ink-400">(optional)</span>
          )}
        </label>

        {description && (
          <p className="text-sm text-ink-600 mb-2">{description}</p>
        )}

        {/* Always render input, just hide it visually when preview exists */}
        <input
          id={uniqueId}
          name={props.name}
          type="file"
          className={previewUrl ? "sr-only" : ""}
          ref={ref}
          accept={props.accept}
          style={{ display: "none" }} // hides the default button
          {...props}
        />

        {previewUrl ? (
          <div className="mt-2 flex flex-col space-y-4">
            <img
              src={previewUrl}
              alt="Preview"
              className="w-full max-w-xs rounded-md object-cover border border-ink-200"
            />
            <div className="flex items-center gap-2">
              <SecondaryButton onClick={handleChange} colour="gray">
                Change
              </SecondaryButton>
              <PrimaryButton colour="red" onClick={handleRemove}>
                Remove
              </PrimaryButton>
            </div>
          </div>
        ) : (
          <div className="mt-2 flex justify-center rounded-lg border border-dashed border-ink-900/25 bg-white px-6 py-10">
            <div className="text-center">
              <PhotoIcon
                aria-hidden="true"
                className="mx-auto size-12 text-ink-300"
              />
              <div className="mt-4 flex text-sm text-ink-600">
                <label
                  htmlFor={uniqueId}
                  className="relative cursor-pointer rounded-md bg-white font-semibold text-accent-600 hover:text-accent-500"
                >
                  <span>Upload a file</span>
                </label>
                <p className="pl-1">or drag and drop</p>
              </div>
              <p className="text-xs text-ink-600">PNG, JPG, GIF up to 10MB</p>
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default ImageUpload;
